import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@phosphor-icons/react/dist/csr/Check';

import { ChallengeForm } from './ChallengeForm';
import { CODE_LENGTH, digitsOnly } from './otpCode';

/**
 * Step two of signing in: the six digits from the authenticator.
 *
 * It renders in place of `LoginPage`, not inside it and not inside `AppShell` —
 * at this point the API has issued no session, so every link the shell offers
 * would 401.
 *
 * The challenge token arrives as a prop and is held by the caller in React
 * state. It is never written to `localStorage`, `sessionStorage` or a cookie:
 * it is a credential with a five-minute life, and persisting it would let it
 * outlive the tab it was issued to and survive the reload that should have
 * ended the attempt. It is also never rendered — it is a credential, not a
 * support reference.
 */

export interface MfaChallengeScreenProps {
  challengeToken: string;
  /** Rejects with an `ApiError`; the form renders the failure. */
  onVerify: (challengeToken: string, code: string) => Promise<unknown>;
  onUseRecoveryCode: () => void;
}

export function MfaChallengeScreen({
  challengeToken,
  onVerify,
  onUseRecoveryCode,
}: MfaChallengeScreenProps) {
  const { t } = useTranslation();

  return (
    <ChallengeForm
      title={t('auth.mfa.title')}
      intro={t('auth.mfa.intro')}
      label={t('auth.mfa.codeLabel')}
      hint={t('auth.mfa.codeHint')}
      numeric
      // The OS keyboard shows digits and the platform offers the code from the
      // notification it just received. `off` would suppress both and buy
      // nothing: the value is single-use and there is no password manager entry
      // for it to leak into.
      autoComplete="one-time-code"
      maxLength={CODE_LENGTH}
      sanitise={digitsOnly}
      canSubmit={(value) => value.length === CODE_LENGTH}
      submitLabel={t('auth.mfa.verify')}
      submitIcon={<CheckIcon size={16} />}
      failureMessage={t('auth.mfa.codeRejected')}
      onSubmit={(code) => onVerify(challengeToken, code)}
      alternate={{ label: t('auth.mfa.useRecoveryCode'), onSelect: onUseRecoveryCode }}
    />
  );
}
